/**
 * The trees report from the crowns' tile archive (`X.pmtiles` beside `X.gpkg`): the map draws
 * from it, and the figures come either from a forest summary's `tree_stats.json` (with the
 * per-tree maps from the archive's points) or, without one or with a boundary bound (a boundary
 * asks for figures over that boundary, which only every crown gives), from the archive's
 * complete zoom of points. Without an archive the report reads the GeoPackage.
 */
import { useEffect, useState } from 'react';
import type { FeatureCollection } from 'geojson';
import { readLayer, tilesAt } from '@/features/reports/growing/archive';
import type { LinkSource } from '@/map/linkSource';
import { openPmtiles, PMTILES_LAYERS, type Archive } from '@/map/pmtiles';
import { normalize, type BaseStats } from './stats';
import { parseTreeStats, summaryStats, TREE_STATS_FILE, type TreeStatsFile } from './summary';

/**
 * The photo archive's points by zoom: z15 holds every crown, coarser zooms a thinned share
 * (forestum's Morton rank: z14 1/2, z13 1/8, z12 1/16, z10–11 1/32).
 */
export const POINT_SAMPLE: Record<number, number> = { 15: 1, 14: 2, 13: 8, 12: 16, 11: 32, 10: 32 };
/** The zoom whose points are every crown. */
export const EVERY_CROWN_ZOOM = 15;
/** At most this many tiles are read for the points next to a summary; a larger area takes a thinner zoom. */
export const POINT_TILES_MAX = 400;

/** The finest zoom whose points cover the archive in at most POINT_TILES_MAX tiles. */
export function pointsZoom(archive: Archive): number {
  const top = Math.min(EVERY_CROWN_ZOOM, archive.header.maxZoom);
  for (let z = top; z > archive.header.minZoom; z--) if (tilesAt(archive, z) <= POINT_TILES_MAX) return z;
  return archive.header.minZoom;
}

export type TreesArchive =
  | { state: 'off' | 'checking' | 'none' }
  | { state: 'ready'; archive: Archive; from: 'summary'; base: BaseStats }
  | { state: 'ready'; archive: Archive; from: 'points'; points: FeatureCollection };

async function fromArchive(archive: Archive, file: TreeStatsFile | null): Promise<TreesArchive> {
  if (file?.units.area) {
    const z = pointsZoom(archive);
    const points = normalize(await readLayer(archive, PMTILES_LAYERS.photo.points, z));
    const base = summaryStats(file, points, 'area', POINT_SAMPLE[z] ?? 1);
    if (base) return { state: 'ready', archive, from: 'summary', base };
  }
  const { minZoom, maxZoom } = archive.header;
  if (minZoom > EVERY_CROWN_ZOOM || maxZoom < EVERY_CROWN_ZOOM) return { state: 'none' };
  const points = await readLayer(archive, PMTILES_LAYERS.photo.points, EVERY_CROWN_ZOOM);
  return { state: 'ready', archive, from: 'points', points };
}

/** `summary` is the forest summary's object; it is not read while `bounded`. */
export function useTreesArchive(trees: { object: string; path: string } | undefined, summary: string | undefined, links: LinkSource, bounded: boolean): TreesArchive {
  const key = trees?.path ? JSON.stringify([trees.object, trees.path, bounded ? '' : (summary ?? '')]) : '';
  const [state, setState] = useState<{ key: string; result: TreesArchive } | null>(null);
  useEffect(() => {
    if (!key) return;
    const [object, path, summaryObject] = JSON.parse(key) as [string, string, string];
    let alive = true;
    const file = summaryObject
      ? links
          .url(summaryObject, TREE_STATS_FILE)
          .then((url) => fetch(url))
          .then((r) => (r.ok ? r.json() : null))
          .then(parseTreeStats)
          .catch(() => null)
      : Promise.resolve(null);
    void Promise.all([file, openPmtiles(object, path, links)])
      .then(([f, a]) => (a ? fromArchive(a, f) : ({ state: 'none' } as const)))
      .catch(() => ({ state: 'none' }) as const)
      .then((result) => alive && setState({ key, result }));
    return () => {
      alive = false;
    };
  }, [key, links]);
  if (!key) return { state: 'off' };
  if (!state || state.key !== key) return { state: 'checking' };
  return state.result;
}
